import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, LogOut, Monitor, Moon, Sun, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAuth } from '@/hooks/useAuth';
import { useTheme, type ThemeMode } from '@/hooks/useTheme';
import { Badge } from '@/components/ui';

const ROLE_LABEL = { super_admin: 'Super admin', admin: 'Admin', cashier: 'Cashier' } as const;

const THEMES: { mode: ThemeMode; label: string; Icon: typeof Sun }[] = [
  { mode: 'light', label: 'Light', Icon: Sun },
  { mode: 'dark', label: 'Dark', Icon: Moon },
  { mode: 'system', label: 'System', Icon: Monitor },
];

export function UserMenu() {
  const { user, logout } = useAuth();
  const { mode, setMode } = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        rootRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!user) return null;
  const itemCls =
    'flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left text-sm hover:bg-surface-3 focus-visible:bg-surface-3';

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((o) => !o)}
        className="flex h-10 items-center gap-2 rounded-lg px-2 hover:bg-surface-3"
      >
        <span className="flex size-8 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-fg">
          {user.name.charAt(0).toUpperCase()}
        </span>
        <span className="hidden text-left leading-tight sm:block">
          <span className="block max-w-32 truncate text-sm font-medium">{user.name}</span>
          <span className="block text-xs text-fg-subtle">{ROLE_LABEL[user.role]}</span>
        </span>
        <ChevronDown aria-hidden className="size-4 text-fg-subtle" />
      </button>

      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label="User menu"
          className="absolute top-full right-0 z-40 mt-1 w-60 rounded-xl border border-border bg-surface p-1.5 shadow-pop"
        >
          <div className="px-3 py-2">
            <p className="truncate text-sm font-semibold">{user.name}</p>
            <p className="truncate text-xs text-fg-subtle">@{user.username}</p>
            <Badge tone="primary" className="mt-1">
              {ROLE_LABEL[user.role]}
            </Badge>
          </div>
          <div role="separator" className="my-1 border-t border-border" />
          <div role="group" aria-label="Theme" className="flex gap-1 px-1.5 py-1">
            {THEMES.map(({ mode: m, label, Icon }) => (
              <button
                key={m}
                type="button"
                role="menuitemradio"
                aria-checked={mode === m}
                onClick={() => setMode(m)}
                className={cn(
                  'flex flex-1 flex-col items-center gap-0.5 rounded-md py-1.5 text-xs',
                  mode === m
                    ? 'bg-primary-soft text-primary-soft-fg'
                    : 'text-fg-muted hover:bg-surface-3',
                )}
              >
                <Icon aria-hidden className="size-4" />
                {label}
              </button>
            ))}
          </div>
          <div role="separator" className="my-1 border-t border-border" />
          <button
            type="button"
            role="menuitem"
            className={itemCls}
            onClick={() => {
              setOpen(false);
              navigate('/change-password');
            }}
          >
            <KeyRound aria-hidden className="size-4" /> Change password
          </button>
          <button
            type="button"
            role="menuitem"
            className={cn(itemCls, 'text-danger')}
            onClick={() => {
              setOpen(false);
              void logout();
            }}
          >
            <LogOut aria-hidden className="size-4" /> Log out
          </button>
        </div>
      )}
    </div>
  );
}
